import {
    Component,
    Input,
    Output,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Renderer2,
    AfterViewInit,
    OnDestroy,
    ElementRef,
    NgZone,
    ViewChildren,
    QueryList,
    EventEmitter,
    ViewEncapsulation,
    Inject,
    Optional,
} from '@angular/core'
import { Observable, Subscriber, Subject } from 'rxjs'
import { debounceTime } from 'rxjs/operators'

import {
    IArea,
    IPoint,
    ISplitSnapshot,
    IAreaSnapshot,
    IOutputData,
    IOutputAreaSizes,
    IDefaultOptions,
} from './split.interface'
import { SplitAreaDirective } from './split-area.directive'
import {
    getInputPositiveNumber,
    getInputBoolean,
    getAreaMinSize,
    getAreaMaxSize,
    getPointFromEvent,
    getElementPixelSize,
    getGutterSideAbsorptionCapacity,
    isUserSizesValid,
    pointDeltaEquals,
    updateAreaSize,
    getKeyboardEndpoint,
    VoidListener,
} from './split.utils'
import { ANGULAR_SPLIT_DEFAULT_OPTIONS } from './split-config.token'
import { HashTable } from '../../../helper/hash-table'
import { DOCUMENT } from '@angular/common'


@Component({
    selector: 'as-split',
    exportAs: 'asSplit',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: [`./split.component.less`],
    templateUrl: './split.component.html',
    encapsulation: ViewEncapsulation.Emulated,
})
export class SplitComponent implements AfterViewInit, OnDestroy {
    @Input() set direction(v: 'horizontal' | 'vertical') {
        this._direction = v === 'vertical' ? 'vertical' : 'horizontal'

        this.renderer.addClass(this.elRef.nativeElement, `as-${this._direction}`)
        this.renderer.removeClass(
            this.elRef.nativeElement,
            `as-${this._direction === 'vertical' ? 'horizontal' : 'vertical'}`,
        )

        this.build(false, false)
    }

    get direction(): 'horizontal' | 'vertical' {
        return this._direction
    }

    @Input() set unit(v: 'percent' | 'pixel') {
        this._unit = v === 'pixel' ? 'pixel' : 'percent'

        this.renderer.addClass(this.elRef.nativeElement, `as-${this._unit}`)
        this.renderer.removeClass(this.elRef.nativeElement, `as-${this._unit === 'pixel' ? 'percent' : 'pixel'}`)

        this.build(false, true)
    }

    get unit(): 'percent' | 'pixel' {
        return this._unit
    }

    @Input() set gutterSize(v: number | null) {
        this._gutterSize = getInputPositiveNumber(v, 11)

        this.build(false, false)
    }

    get gutterSize(): number | null {
        return this._gutterSize
    }

    @Input() set gutterStep(v: number) {
        this._gutterStep = getInputPositiveNumber(v, 1)
    }

    get gutterStep(): number {
        return this._gutterStep
    }

    @Input() set restrictMove(v: boolean) {
        this._restrictMove = getInputBoolean(v)
    }

    get restrictMove(): boolean {
        return this._restrictMove
    }

    @Input() set useTransition(v: boolean) {
        this._useTransition = getInputBoolean(v)

        if (this._useTransition) {
            this.renderer.addClass(this.elRef.nativeElement, 'as-transition')
        } else {
            this.renderer.removeClass(this.elRef.nativeElement, 'as-transition')
        }
    }

    get useTransition(): boolean {
        return this._useTransition
    }

    @Input() set disabled(v: boolean) {
        this._disabled = getInputBoolean(v)

        if (this._disabled) {
            this.renderer.addClass(this.elRef.nativeElement, 'as-disabled')
        } else {
            this.renderer.removeClass(this.elRef.nativeElement, 'as-disabled')
        }
    }

    get disabled(): boolean {
        return this._disabled
    }

    @Input() set dir(v: 'ltr' | 'rtl') {
        this._dir = v === 'rtl' ? 'rtl' : 'ltr'

        this.renderer.setAttribute(this.elRef.nativeElement, 'dir', this._dir)
    }

    get dir(): 'ltr' | 'rtl' {
        return this._dir
    }

    @Input() set gutterDblClickDuration(v: number) {
        this._gutterDblClickDuration = getInputPositiveNumber(v, 0)
    }

    @Input() gutterClickDeltaPx = 2

    @Input() gutterAriaLabel?: string;

    mouseMoveListener: VoidListener;

    mouseUpListener: VoidListener;

    get gutterDblClickDuration(): number {
        return this._gutterDblClickDuration
    }
    @Output() get transitionEnd(): Observable<IOutputAreaSizes> {
        return new Observable<IOutputAreaSizes>(
            (subscriber: Subscriber<IOutputAreaSizes>) => (this.transitionEndSubscriber = subscriber),
        ).pipe(debounceTime<IOutputAreaSizes>(20))
    }

    private _config: IDefaultOptions = {
        direction: 'horizontal',
        unit: 'percent',
        gutterSize: 11,
        gutterStep: 1,
        restrictMove: false,
        useTransition: false,
        disabled: false,
        dir: 'ltr',
        gutterDblClickDuration: 0,
    }

    constructor(
        @Inject(DOCUMENT) private document: Window,
        private ngZone: NgZone,
        private elRef: ElementRef,
        private cdRef: ChangeDetectorRef,
        private renderer: Renderer2,
        @Optional() @Inject(ANGULAR_SPLIT_DEFAULT_OPTIONS) globalConfig: IDefaultOptions,
    ) {
        // To force adding default class, could be override by user @Input() or not
        this.direction = this._direction
        this._config = globalConfig ? Object.assign(this._config, globalConfig) : this._config
        Object.keys(this._config).forEach((property) => {
            let cfg = this._config as HashTable<any>;
            let me = this as HashTable<any>;
            me[property] = cfg[property];
        })
    }
    private _direction!: 'horizontal' | 'vertical'

    private _unit!: 'percent' | 'pixel'

    private _gutterSize!: number

    private _gutterStep!: number

    private _restrictMove!: boolean

    private _useTransition!: boolean

    private _disabled!: boolean

    private _dir!: 'ltr' | 'rtl'

    private _gutterDblClickDuration!: number

    @Output() dragStart = new EventEmitter<IOutputData>(false)
    @Output() dragEnd = new EventEmitter<IOutputData>(false)
    @Output() gutterClick = new EventEmitter<IOutputData>(false)
    @Output() gutterDblClick = new EventEmitter<IOutputData>(false)
    @Input() isEdit: boolean = false;
    @Output() gutterAddClick = new EventEmitter<IOutputData>(false)

    private transitionEndSubscriber!: Subscriber<IOutputAreaSizes>

    private dragProgressSubject: Subject<IOutputData> = new Subject()
    dragProgress$: Observable<IOutputData> = this.dragProgressSubject.asObservable()

    private isDragging = false
    private isWaitingClear = false
    //private isWaitingInitialMove = false
    private dragListeners: Array<Function> = []
    private snapshot: ISplitSnapshot | null = null
    private startPoint: IPoint | null = null
    private endPoint: IPoint | null = null

    public readonly displayedAreas: Array<IArea> = []
    private readonly hiddenAreas: Array<IArea> = [];
    private inited: boolean = false;

    @ViewChildren('gutterEls') private gutterEls!: QueryList<ElementRef>

    _clickTimeout: number | null = null

    public ngAfterViewInit() {
        //console.log("SplitComponent ngAfterViewInit");
        this.ngZone.runOutsideAngular(() => {
            //console.log('ngZone.ngAfterInit()');
            // To avoid transition at first rendering
            setTimeout(() => {
                this.renderer.addClass(this.elRef.nativeElement, 'as-init');
                this.inited = true;
            });
        })
    }

    private getNbGutters(): number {
        return this.displayedAreas.length === 0 ? 0 : this.displayedAreas.length - 1
    }

    public addArea(component: SplitAreaDirective): void {
        const newArea: IArea = {
            component,
            order: 0,
            size: 0,
            minSize: null,
            maxSize: null,
            sizeBeforeCollapse: null,
            gutterBeforeCollapse: 0,
        }

        if (component.visible === true) {
            this.displayedAreas.push(newArea)

            this.build(true, true)
        } else {
            this.hiddenAreas.push(newArea)
        }
    }

    public removeArea(component: SplitAreaDirective): void {
        if (this.displayedAreas.some((a) => a.component === component)) {
            const area = this.displayedAreas.find((a) => a.component === component)
            if (area != null)
                this.displayedAreas.splice(this.displayedAreas.indexOf(area), 1)

            this.build(true, true)
        } else if (this.hiddenAreas.some((a) => a.component === component)) {
            const area = this.hiddenAreas.find((a) => a.component === component)
            if (area != null)
                this.hiddenAreas.splice(this.hiddenAreas.indexOf(area), 1)
        }
    }

    public updateArea(component: SplitAreaDirective, resetOrders: boolean, resetSizes: boolean): void {
        if (component.visible === true) {
            this.build(resetOrders, resetSizes)
        }
    }

    public showArea(component: SplitAreaDirective): void {
        const area = this.hiddenAreas.find((a) => a.component === component)
        if (area === undefined) {
            return
        }

        const areas = this.hiddenAreas.splice(this.hiddenAreas.indexOf(area), 1)
        this.displayedAreas.push(...areas)

        this.build(true, true)
    }

    public hideArea(comp: SplitAreaDirective): void {
        const area = this.displayedAreas.find((a) => a.component === comp)
        if (area === undefined) {
            return
        }

        const areas = this.displayedAreas.splice(this.displayedAreas.indexOf(area), 1)
        areas.forEach((item) => {
            item.order = 0
            item.size = 0
        })
        this.hiddenAreas.push(...areas)

        this.build(true, true)
    }

    public getVisibleAreaSizes(): IOutputAreaSizes {
        return this.displayedAreas.map((a) => (a.size === null ? '*' : a.size))
    }

    public setVisibleAreaSizes(sizes: IOutputAreaSizes): boolean {
        if (sizes.length !== this.displayedAreas.length) {
            return false
        }

        const formattedSizes = sizes.map((s) => getInputPositiveNumber(s, null))
        const isValid = isUserSizesValid(this.unit, formattedSizes)

        if (isValid === false) {
            return false
        }

        // @ts-ignore
        this.displayedAreas.forEach((area, i) => (area.component._size = formattedSizes[i]))

        this.build(false, true)
        return true
    }

    private build(resetOrders: boolean, resetSizes: boolean): void {
        this.stopDragging()

        // ¤ AREAS ORDER

        if (resetOrders === true) {
            // If user provided 'order' for each area, use it to sort them.
            if (this.displayedAreas.every((a) => a.component.order !== null)) {
                this.displayedAreas.sort((a, b) => <number>a.component.order - <number>b.component.order)
            }

            // Then set real order with multiples of 2, numbers between will be used by gutters.
            this.displayedAreas.forEach((area, i) => {
                area.order = i * 2
                area.component.setStyleOrder(area.order)
            })
        }

        // ¤ AREAS SIZE

        if (resetSizes === true) {
            const useUserSizes = isUserSizesValid(
                this.unit,
                this.displayedAreas.map((a) => a.component.size),
            )

            switch (this.unit) {
                case 'percent': {
                    const defaultSize = 100 / this.displayedAreas.length

                    this.displayedAreas.forEach((area) => {
                        area.size = useUserSizes ? <number>area.component.size : defaultSize
                        area.minSize = getAreaMinSize(area)
                        area.maxSize = getAreaMaxSize(area)
                    })
                    break
                }
                case 'pixel': {
                    if (useUserSizes) {
                        this.displayedAreas.forEach((area) => {
                            area.size = area.component.size
                            area.minSize = getAreaMinSize(area)
                            area.maxSize = getAreaMaxSize(area)
                        })
                    } else {
                        const wildcardSizeAreas = this.displayedAreas.filter((a) => a.component.size === null)

                        // No wildcard area > Need to select one arbitrarily > first
                        if (wildcardSizeAreas.length === 0 && this.displayedAreas.length > 0) {
                            this.displayedAreas.forEach((area, i) => {
                                area.size = i === 0 ? null : area.component.size
                                area.minSize = i === 0 ? null : getAreaMinSize(area)
                                area.maxSize = i === 0 ? null : getAreaMaxSize(area)
                            })
                        } else if (wildcardSizeAreas.length > 1) {
                            // More than one wildcard area > Need to keep only one arbitrarily > first
                            let alreadyGotOne = false
                            this.displayedAreas.forEach((area) => {
                                if (area.component.size === null) {
                                    if (alreadyGotOne === false) {
                                        area.size = null
                                        area.minSize = null
                                        area.maxSize = null
                                        alreadyGotOne = true
                                    } else {
                                        area.size = 100
                                        area.minSize = null
                                        area.maxSize = null
                                    }
                                } else {
                                    area.size = area.component.size
                                    area.minSize = getAreaMinSize(area)
                                    area.maxSize = getAreaMaxSize(area)
                                }
                            })
                        }
                    }
                    break
                }
            }
        }

        this.refreshStyleSizes()
        this.cdRef.markForCheck()
    }

    private refreshStyleSizes(): void {
        ///////////////////////////////////////////
        // PERCENT MODE
        if (this.unit === 'percent') {
            // Only one area > flex-basis 100%
            if (this.displayedAreas.length === 1) {
                this.displayedAreas[0].component.setStyleFlex(0, 0, `100%`, false, false)
            } else {
                // Multiple areas > use each percent basis
                const sumGutterSize = this.getNbGutters() * (this.gutterSize as number)

                this.displayedAreas.forEach((area) => {
                    area.component.setStyleFlex(
                        0,
                        0,
                        `calc( ${area.size}% - ${(<number>area.size / 100) * sumGutterSize}px )`,
                        area.minSize !== null && area.minSize === area.size,
                        area.maxSize !== null && area.maxSize === area.size,
                    )
                })
            }
        } else if (this.unit === 'pixel') {
            ///////////////////////////////////////////
            // PIXEL MODE
            this.displayedAreas.forEach((area) => {
                // Area with wildcard size
                if (area.size === null) {
                    if (this.displayedAreas.length === 1) {
                        area.component.setStyleFlex(1, 1, `100%`, false, false)
                    } else {
                        area.component.setStyleFlex(1, 1, `auto`, false, false)
                    }
                } else {
                    // Area with pixel size
                    // Only one area > flex-basis 100%
                    if (this.displayedAreas.length === 1) {
                        area.component.setStyleFlex(0, 0, `100%`, false, false)
                    } else {
                        // Multiple areas > use each pixel basis
                        area.component.setStyleFlex(
                            0,
                            0,
                            `${area.size}px`,
                            area.minSize !== null && area.minSize === area.size,
                            area.maxSize !== null && area.maxSize === area.size,
                        )
                    }
                }
            })
        }
    }

    public endMouseDrag(event: PointerEvent | TouchEvent, gutterNum: number): void {
        const tempPoint = getPointFromEvent(event);
        // Be sure mouseup/touchend happened if touch/cursor is not moved.
        if (
            this.startPoint &&
            pointDeltaEquals(this.startPoint, tempPoint as IPoint, this.gutterClickDeltaPx) &&
            (!this.isDragging /*|| this.isWaitingInitialMove*/)
        ) {
            // If timeout in progress and new click > clearTimeout & dblClickEvent
            if (this._clickTimeout !== null) {
                window.clearTimeout(this._clickTimeout)
                this._clickTimeout = null
                this.notify('dblclick', gutterNum)
                this.stopDragging()
            } else {
                // Else start timeout to call clickEvent at end
                this._clickTimeout = window.setTimeout(() => {
                    this._clickTimeout = null
                    this.notify('click', gutterNum)
                    this.stopDragging()
                }, this.gutterDblClickDuration)
            }
        }
    }


    public startMouseDrag(event: PointerEvent | TouchEvent, gutterOrder: number, gutterNum: number, touch: boolean = false): void {
        event.stopPropagation()
        if (this.isDragging) {
            console.warn('already draging');
            return;
        }
        if (!this.inited) {
            console.warn('no init');
            return;
        }
        //console.log(`start drag ${touch}`);

        this.startPoint = getPointFromEvent(event);

        this.setupForDragEvent(gutterOrder, gutterNum);


        this.bindMouseListeners(touch);

        this.startDragging();
        event.preventDefault();
    }

    private setupForDragEvent(gutterOrder: number, gutterNum: number) {
        this.snapshot = {
            gutterNum,
            lastSteppedOffset: 0,
            allAreasSizePixel: getElementPixelSize(this.elRef, this.direction) - this.getNbGutters() * (this.gutterSize as number),
            allInvolvedAreasSizePercent: 100,
            areasBeforeGutter: [],
            areasAfterGutter: [],
        }

        this.displayedAreas.forEach((area) => {
            const areaSnapshot: IAreaSnapshot = {
                area,
                sizePixelAtStart: getElementPixelSize(area.component.elRef, this.direction),
                sizePercentAtStart: this.unit === 'percent' ? (area.size as number) : -1, // If pixel mode, anyway, will not be used.
            }

            if (this.snapshot == null)
                return;

            if (area.order < gutterOrder) {
                if (this.restrictMove === true) {
                    this.snapshot.areasBeforeGutter = [areaSnapshot]
                } else {
                    this.snapshot.areasBeforeGutter.unshift(areaSnapshot)
                }
            } else if (area.order > gutterOrder) {
                if (this.restrictMove === true) {
                    if (this.snapshot.areasAfterGutter.length === 0) {
                        this.snapshot.areasAfterGutter = [areaSnapshot]
                    }
                } else {
                    this.snapshot.areasAfterGutter.push(areaSnapshot)
                }
            }
        })

        this.snapshot.allInvolvedAreasSizePercent = [
            ...this.snapshot.areasBeforeGutter,
            ...this.snapshot.areasAfterGutter,
        ].reduce((t, a) => t + a.sizePercentAtStart, 0)

        if (this.snapshot.areasBeforeGutter.length === 0 || this.snapshot.areasAfterGutter.length === 0) {
            return
        }
    }

    private startDragging() {
        this.displayedAreas.forEach((area) => area.component.lockEvents());

        this.isDragging = true;
        //this.isWaitingInitialMove = true;

        this.ngZone.run(() => {
            //console.log('ngZone.drag()');
            //this.isWaitingInitialMove = false;

            this.renderer.addClass(this.elRef.nativeElement, 'as-dragging')
            if (this.snapshot == null) {
                console.log('no snap')
                return;
            }

            this.renderer.addClass(this.gutterEls.toArray()[this.snapshot.gutterNum - 1].nativeElement, 'as-dragged')

            this.notify('start', this.snapshot.gutterNum)
        });
    }

    private mouseDragEvent(event: PointerEvent | TouchEvent): void {
        const tempPoint = getPointFromEvent(event)
        if (this._clickTimeout !== null && !pointDeltaEquals(this.startPoint as IPoint, tempPoint as IPoint, this.gutterClickDeltaPx)) {
            window.clearTimeout(this._clickTimeout)
            this._clickTimeout = null;
        }

        if (!this.isDragging)
            return;

        this.endPoint = getPointFromEvent(event)
        if (!this.endPoint)
            return;

        this.drag();
    }
    private drag() {
        if (this.startPoint == null || this.endPoint == null)
            return;

        let offset =
            this.direction === 'horizontal' ? this.startPoint.x - this.endPoint.x : this.startPoint.y - this.endPoint.y;

        // RTL requires negative offset only in horizontal mode as in vertical
        // RTL has no effect on drag direction.
        if (this.dir === 'rtl' && this.direction === 'horizontal') {
            offset = -offset
        }

        const steppedOffset = Math.round(offset / this.gutterStep) * this.gutterStep

        if (this.snapshot == null || steppedOffset === this.snapshot.lastSteppedOffset) {
            return
        }

        this.snapshot.lastSteppedOffset = steppedOffset

        // Need to know if each gutter side areas could reacts to steppedOffset

        let areasBefore = getGutterSideAbsorptionCapacity(
            this.unit,
            this.snapshot.areasBeforeGutter,
            -steppedOffset,
            this.snapshot.allAreasSizePixel,
        )
        let areasAfter = getGutterSideAbsorptionCapacity(
            this.unit,
            this.snapshot.areasAfterGutter,
            steppedOffset,
            this.snapshot.allAreasSizePixel,
        )

        // Each gutter side areas can't absorb all offset
        if (areasBefore.remain !== 0 && areasAfter.remain !== 0) {
            if (Math.abs(areasBefore.remain) === Math.abs(areasAfter.remain)) {
            } else if (Math.abs(areasBefore.remain) > Math.abs(areasAfter.remain)) {
                areasAfter = getGutterSideAbsorptionCapacity(
                    this.unit,
                    this.snapshot.areasAfterGutter,
                    steppedOffset + areasBefore.remain,
                    this.snapshot.allAreasSizePixel,
                )
            } else {
                areasBefore = getGutterSideAbsorptionCapacity(
                    this.unit,
                    this.snapshot.areasBeforeGutter,
                    -(steppedOffset - areasAfter.remain),
                    this.snapshot.allAreasSizePixel,
                )
            }
        } else if (areasBefore.remain !== 0) {
            // Areas before gutter can't absorbs all offset > need to recalculate sizes for areas after gutter.
            areasAfter = getGutterSideAbsorptionCapacity(
                this.unit,
                this.snapshot.areasAfterGutter,
                steppedOffset + areasBefore.remain,
                this.snapshot.allAreasSizePixel,
            )
        } else if (areasAfter.remain !== 0) {
            // Areas after gutter can't absorbs all offset > need to recalculate sizes for areas before gutter.
            areasBefore = getGutterSideAbsorptionCapacity(
                this.unit,
                this.snapshot.areasBeforeGutter,
                -(steppedOffset - areasAfter.remain),
                this.snapshot.allAreasSizePixel,
            )
        }

        if (this.unit === 'percent') {
            // Hack because of browser messing up with sizes using calc(X% - Ypx) -> el.getBoundingClientRect()
            // If not there, playing with gutters makes total going down to 99.99875% then 99.99286%, 99.98986%,..
            const all = [...areasBefore.list, ...areasAfter.list]
            const areaToReset = all.find(
                (a) =>
                    a.percentAfterAbsorption !== 0 &&
                    a.percentAfterAbsorption !== a.areaSnapshot.area.minSize &&
                    a.percentAfterAbsorption !== a.areaSnapshot.area.maxSize,
            )

            if (areaToReset) {
                areaToReset.percentAfterAbsorption =
                    this.snapshot.allInvolvedAreasSizePercent -
                    all.filter((a) => a !== areaToReset).reduce((total, a) => total + a.percentAfterAbsorption, 0)
            }
        }

        // Now we know areas could absorb steppedOffset, time to really update sizes

        areasBefore.list.forEach((item) => updateAreaSize(this.unit, item))
        areasAfter.list.forEach((item) => updateAreaSize(this.unit, item))

        this.refreshStyleSizes()
        this.notify('progress', this.snapshot.gutterNum)
    }

    private stopDragging(event?: Event): void {
        if (event) {
            event.preventDefault()
            event.stopPropagation()
        }

        if (this.isDragging === false) {
            return
        }

        this.displayedAreas.forEach((area) => area.component.unlockEvents())

        while (this.dragListeners.length > 0) {
            const fct = this.dragListeners.pop()
            if (fct) {
                fct()
            }
        }

        // Warning: Have to be before "notify('end')"
        // because "notify('end')"" can be linked to "[size]='x'" > "build()" > "stopDragging()"
        this.isDragging = false

        // If moved from starting point, notify end
        if (this.snapshot != null/* && this.isWaitingInitialMove === false*/) {
            this.notify('end', this.snapshot.gutterNum)
        }

        this.renderer.removeClass(this.elRef.nativeElement, 'as-dragging')
        if (this.snapshot != null)
            this.renderer.removeClass(this.gutterEls.toArray()[this.snapshot.gutterNum - 1].nativeElement, 'as-dragged')
        this.snapshot = null
        this.isWaitingClear = true

        // Needed to let (click)="clickGutter(...)" event run and verify if mouse moved or not
        this.ngZone.runOutsideAngular(() => {
            setTimeout(() => {
                /*console.log('runOutsideAngular');*/
                this.startPoint = null
                this.endPoint = null
                this.isWaitingClear = false
            })
        })
    }

    public notify(type: 'start' | 'progress' | 'end' | 'click' | 'dblclick' | 'transitionEnd' | 'add', gutterNum: number): void {
        const sizes = this.getVisibleAreaSizes();

        if (type === 'start') {
            this.dragStart.emit({ gutterNum, sizes })
        } else if (type === 'end') {
            this.dragEnd.emit({ gutterNum, sizes })
        } else if (type === 'click') {
            this.gutterClick.emit({ gutterNum, sizes })
        } else if (type === 'dblclick') {
            this.gutterDblClick.emit({ gutterNum, sizes })
        } else if (type === 'transitionEnd') {
            if (this.transitionEndSubscriber) {
                //console.log('ngZone.notify()');
                this.ngZone.run(() => this.transitionEndSubscriber.next(sizes))
            }
        } else if (type === 'progress') {
            // Stay outside zone to allow users do what they want about change detection mechanism.
            this.dragProgressSubject.next({ gutterNum, sizes })
        }

        if (type === 'add')
            this.gutterAddClick.emit({ gutterNum, sizes });
    }

    public ngOnDestroy(): void {
        this.stopDragging()
    }

    public collapseArea(comp: SplitAreaDirective, newSize: number, gutter: 'left' | 'right'): void {
        const area = this.displayedAreas.find((a) => a.component === comp)
        if (area === undefined) {
            return
        }
        const whichGutter = gutter === 'right' ? 1 : -1
        if (!area.sizeBeforeCollapse) {
            area.sizeBeforeCollapse = area.size
            area.gutterBeforeCollapse = whichGutter
        }
        area.size = newSize
        const gtr = this.gutterEls.find((f) => f.nativeElement.style.order === `${area.order + whichGutter}`)
        if (gtr) {
            this.renderer.addClass(gtr.nativeElement, 'as-split-gutter-collapsed')
        }
        this.updateArea(comp, false, false)
    }

    public expandArea(comp: SplitAreaDirective): void {
        const area = this.displayedAreas.find((a) => a.component === comp)
        if (area === undefined) {
            return
        }
        if (!area.sizeBeforeCollapse) {
            return
        }
        area.size = area.sizeBeforeCollapse
        area.sizeBeforeCollapse = null
        const gtr = this.gutterEls.find((f) => f.nativeElement.style.order === `${area.order + area.gutterBeforeCollapse}`)
        if (gtr) {
            this.renderer.removeClass(gtr.nativeElement, 'as-split-gutter-collapsed')
        }
        this.updateArea(comp, false, false)
    }

    public getAriaAreaSizeText(size: number | null): string | null {
        if (size === null) {
            return null
        }

        return size.toFixed(0) + ' ' + this.unit
    }

    add = (event: PointerEvent | TouchEvent, gutterNum: number) =>
        this.notify('add', gutterNum);


    bindMouseListeners(touch: boolean) {
        if (!this.mouseMoveListener) {
            if (touch)
                this.mouseMoveListener = this.renderer.listen('document', 'touchmove', (event) => {
                    this.mouseDragEvent(event);
                });
            else
                this.mouseMoveListener = this.renderer.listen('document', 'pointermove', (event) => {
                    this.mouseDragEvent(event);
                });
        }

        if (!this.mouseUpListener) {
            this.mouseUpListener = this.renderer.listen('document', 'pointerup', (event) => {
                this.stopDragging(event);
                this.unbindMouseListeners();
            });
        }
    }

    unbindMouseListeners() {
        if (this.mouseMoveListener) {
            this.mouseMoveListener();
            this.mouseMoveListener = null;
        }

        if (this.mouseUpListener) {
            this.mouseUpListener();
            this.mouseUpListener = null;
        }
    }

}
